import { formatMoney } from '@theme/money-formatting';

/**
 * Kaching-packs in de vorm van het PDP-redesign (§1.5). De look staat in
 * assets/kaching-tiers.css; dit script doet wat CSS niet kan:
 *
 * - de besparing in euro ("Bespaar € 5,95"): het verschil tussen de prijs en de
 *   volle prijs die Kaching zelf op de kaart zet, dus altijd uit de Kaching-deal en
 *   nooit hardcoded;
 * - geur per box: bij 2 of meer boxen een rij per box in de gekozen kaart, met de
 *   geuren als kleine packshots, zodat je ziet wat je koopt. Eén tik wisselt de
 *   geur. Box 1 is het product van de pagina; dat wissel je met de geurknoppen
 *   erboven. (Dit wijkt bewust af van het ontwerp, dat chips met een link naar
 *   Kaching's keuzevenster had: te veel klikken, en een zoekvenster met volle
 *   producttitels voor een keuze uit zes geuren.)
 * - een labelregel boven de kaarten met de huidige keuze ("Kies je aantal: 3 Giftboxes"),
 *   net als "Kies jouw geur: Reset Mix" boven de geurknoppen. Die staat vóór Kaching's
 *   element, niet erin, zodat Kaching hem nooit weg kan renderen;
 * - "t.w.v." voor de waarde van een gratis product, en een zichtbaar label bij
 *   Kaching's aantalkiezer;
 * - pdp_pack_select en pdp_box_scent voor het meetplan.
 *
 * Het wisselen zelf blijft van Kaching, zodat de winkelwagen precies krijgt wat Kaching
 * verwacht: het script klikt Kaching's eigen verwijderknop, daarna de kiesknop die op
 * die plek verschijnt, en kiest de geur in Kaching's keuzevenster — dat zolang
 * onzichtbaar blijft. Staat de geur daar niet in, dan wordt het venster gewoon
 * zichtbaar en kiest de bezoeker zelf; sluiten zonder kiezen zet de oude geur terug.
 *
 * Kaching rendert asynchroon en kan zijn kaarten opnieuw opbouwen, dus alles hier is
 * idempotent en draait opnieuw op elke wijziging. Teksten en de geldnotatie rijden
 * mee op de scripttag (snippets/scripts.liquid); de geuren komen van het geurblok
 * (data-swatch-data).
 */

const config = document.getElementById('kaching-tiers-js')?.dataset ?? {};

const ITEM = '.kaching-bundles__collection-product';
const REMOVE = '.kaching-bundles__collection-product__remove-button';
const CHOOSE = '.kaching-bundles__collection-product__choose-product-button';
const MODAL = '.kaching-bundles__choose-product-modal';
const AUTO_PICK = 'kaching-auto-pick';

/** "€ 1.234,56" / "€1,234.56" → centen; null als er geen bedrag in staat. */
function toCents(text) {
  const match = (text || '').match(/\d[\d.,\s]*/);
  if (!match) return null;
  const raw = match[0].replace(/\s/g, '');
  const decimals = raw.match(/[.,](\d{2})$/);
  const whole = (decimals ? raw.slice(0, -3) : raw).replace(/[.,]/g, '');
  const cents = parseInt(whole || '0', 10) * 100 + (decimals ? parseInt(decimals[1], 10) : 0);
  return Number.isFinite(cents) ? cents : null;
}

/** @returns {{handle: string, name: string, image: string, fill: string, units: number, available: boolean}[]} */
function swatches() {
  try {
    return JSON.parse(document.querySelector('[data-swatch-data]')?.getAttribute('data-swatch-data') || '[]');
  } catch (error) {
    return [];
  }
}

function setAttr(el, name, value) {
  if (!el) return;
  if (value == null || value === '') {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value);
  }
}

const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));

/** Wacht tot test() iets oplevert, of geef na `ms` op. */
async function waitFor(test, ms = 2500) {
  const end = performance.now() + ms;
  while (performance.now() < end) {
    const result = test();
    if (result) return result;
    await nextFrame();
  }
  return null;
}

function publish(name, payload) {
  try {
    window.Shopify?.analytics?.publish(name, { product: config.product, ...payload });
  } catch (error) {
    // tracking mag de packkeuze nooit in de weg zitten
  }
}

/* ── Besparing en cadeau ─────────────────────────────────────────────────────── */

/** @param {Element} bar */
function enhanceSavings(bar) {
  const pricing = bar.querySelector('.kaching-bundles__bar-pricing');
  const price = toCents(bar.querySelector('.kaching-bundles__bar-price')?.textContent);
  const full = toCents(bar.querySelector('.kaching-bundles__bar-full-price')?.textContent);
  let saving = '';
  if (price != null && full != null && full > price && config.saving && config.moneyFormat && config.currency) {
    saving = config.saving.replace('%%AMOUNT%%', formatMoney(full - price, config.moneyFormat, config.currency));
  }
  setAttr(pricing, 'data-saving', saving);
}

/** @param {Element} bar */
function enhanceExtras(bar) {
  for (const value of bar.querySelectorAll('.kaching-bundles__free-gift__full-price')) {
    setAttr(value, 'data-prefix', config.worth || null);
  }
  // Kaching's aantalkiezer ("− 3 +") krijgt zijn eigen woord als zichtbaar label.
  const stepper = bar.querySelector('.kaching-bundles__quantity-selector');
  const word = stepper?.querySelector('input')?.getAttribute('aria-label');
  setAttr(stepper, 'data-label', word || null);
}

/* ── Geur per box ────────────────────────────────────────────────────────────── */

/** @param {Element} item */
function itemHandle(item) {
  if (item.querySelector(CHOOSE)) return '';
  const href = item.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
  return href.match(/\/products\/([^/?#]+)/)?.[1] || config.product || '';
}

/** @param {Element} bar */
function items(bar) {
  return [...bar.querySelectorAll(`.kaching-bundles__bar-collection-products > ${ITEM}`)];
}

/**
 * De rijen in de gekozen kaart. Elke render bouwt ze opnieuw op uit wat Kaching nu
 * toont, maar alleen als er iets veranderd is — anders zou de focus van een stip
 * verspringen.
 * @param {Element} bar
 */
function renderBoxes(bar) {
  const wrapper = bar.querySelector('.kaching-bundles__bar-wrapper');
  let panel = wrapper?.querySelector(':scope > .box-scents');
  const boxes = items(bar);
  const selected = bar.classList.contains('kaching-bundles__bar--selected');

  if (!wrapper || !selected || boxes.length < 2) {
    panel?.remove();
    setAttr(bar, 'data-box-scents', null);
    return;
  }

  const all = swatches();
  const current = all.find((s) => s.handle === config.product);
  // Alleen geuren in hetzelfde formaat: een 30-pack is geen box van een 5-pack-deal.
  const options = all.filter((s) => !current || !s.units || !current.units || s.units === current.units);
  const handles = boxes.map(itemHandle);
  const signature = JSON.stringify([handles, options.map((s) => s.handle)]);

  if (panel && panel.dataset.signature === signature) return;
  if (!panel) {
    panel = document.createElement('div');
    panel.className = 'box-scents';
    wrapper.append(panel);
  }
  panel.dataset.signature = signature;
  setAttr(bar, 'data-box-scents', 'true');

  const focused = document.activeElement?.closest?.('.box-scents__option');
  const focusKey = focused ? `${focused.dataset.box}:${focused.dataset.handle}` : '';

  panel.replaceChildren(
    ...handles.map((handle, index) => {
      // Twee regels per box: "Box 2 · Rose" en daaronder de stippen over de volle
      // breedte, zodat elke stip een tikvlak van 44px kan hebben, ook op 360px.
      const row = document.createElement('div');
      row.className = 'box-scents__row';
      const label = `${config.item || ''} ${index + 1}`.trim();
      const swatch = all.find((s) => s.handle === handle);

      const head = document.createElement('p');
      head.className = 'box-scents__head';

      const name = document.createElement('span');
      name.className = 'box-scents__label';
      name.textContent = `${label} · `;

      const chosen = document.createElement('span');
      chosen.className = 'box-scents__name';
      chosen.textContent = swatch?.name || (handle ? '' : config.choose || '');
      if (!handle) chosen.classList.add('box-scents__name--empty');

      if (index === 0) {
        // Box 1 is het product van deze pagina: een mini-packshot en de naam.
        head.append(name, thumb(swatch, 'box-scents__mini'), chosen);
        if (config.first) {
          const hint = document.createElement('span');
          hint.className = 'box-scents__hint';
          hint.textContent = ` · ${config.first}`;
          head.append(hint);
        }
        row.append(head);
        return row;
      }

      head.append(name, chosen);
      const choices = document.createElement('div');
      choices.className = 'box-scents__options';
      choices.setAttribute('role', 'group');
      choices.setAttribute('aria-label', label);
      for (const option of options) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'box-scents__option';
        button.dataset.box = String(index);
        button.dataset.handle = option.handle;
        button.setAttribute('aria-label', option.name);
        button.setAttribute('aria-pressed', String(option.handle === handle));
        button.title = option.name;
        if (option.handle === handle) button.classList.add('is-on');
        if (!option.available) button.disabled = true;
        button.append(thumb(option, 'box-scents__thumb'));
        choices.append(button);
      }
      row.append(head, choices);
      return row;
    })
  );

  if (focusKey) {
    const [box, handle] = focusKey.split(':');
    panel.querySelector(`.box-scents__option[data-box="${box}"][data-handle="${handle}"]`)?.focus();
  }
}

/**
 * Packshot van een geur; zonder afbeelding de verpakkingskleur als vlak.
 * @param {{name?: string, image?: string, fill?: string} | undefined} swatch
 */
function thumb(swatch, className) {
  if (swatch?.image) {
    const img = document.createElement('img');
    img.className = className;
    img.src = swatch.image;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 48;
    img.height = 48;
    return img;
  }
  const span = document.createElement('span');
  span.className = `${className} ${className}--fill`;
  span.style.setProperty('--fill', swatch?.fill || '#d0d0d0');
  span.setAttribute('aria-hidden', 'true');
  return span;
}

/** De box die nu van geur wisselt: om bij annuleren de oude geur terug te kiezen. */
let pendingSwap = null;
let busy = false;

/** Kies in Kaching's keuzevenster het product met deze handle. */
function pickInModal(handle) {
  const modal = document.querySelector(MODAL);
  const product = modal
    ? [...modal.querySelectorAll('.kaching-bundles__choose-product__product')].find((el) =>
        el.querySelector(`a[href*="/products/${handle}?"], a[href$="/products/${handle}"]`)
      )
    : null;
  const pick = product?.querySelector('.kaching-bundles__choose-product__product-button');
  if (!(pick instanceof HTMLElement)) return false;
  pick.click();
  return true;
}

/**
 * Zet box `index` van de gekozen kaart op `handle`, via Kaching's eigen knoppen.
 * @param {Element} bar @param {number} index @param {string} handle
 */
async function setBox(bar, index, handle, { silent = false } = {}) {
  if (busy) return;
  const box = items(bar)[index];
  if (!box || itemHandle(box) === handle) return;
  const previous = itemHandle(box);

  busy = true;
  bar.querySelector('.box-scents')?.classList.add('is-busy');
  document.documentElement.classList.add(AUTO_PICK);
  try {
    box.querySelector(REMOVE)?.click();
    const choose = await waitFor(() => items(bar)[index]?.querySelector(CHOOSE), 1500);
    if (!(choose instanceof HTMLElement)) return;
    choose.click();

    // Kaching haalt de producten soms nog op; wacht tot de lijst er staat.
    const picked = await waitFor(() => pickInModal(handle), 3000);
    if (picked) {
      if (!silent) publish('pdp_box_scent', { box: index + 1, to: handle });
      return;
    }
    // Niet gevonden: laat Kaching's venster zien, de bezoeker kiest zelf.
    pendingSwap = previous ? { handle: previous } : null;
    document.documentElement.classList.remove(AUTO_PICK);
  } finally {
    await waitFor(() => !document.querySelector(MODAL) || pendingSwap, 1500);
    document.documentElement.classList.remove(AUTO_PICK);
    bar.querySelector('.box-scents')?.classList.remove('is-busy');
    busy = false;
  }
}

/**
 * Een kaart met lege boxen vullen met het product van deze pagina, zoals Kaching dat
 * bij de steamers zelf doet. Zonder dit zet "In winkelwagen" bij de oogmaskers op
 * "15 Maskers" maar één pack plus het cadeau in de winkelwagen, het cadeau tegen de
 * volle prijs, want de deal is dan niet compleet. Eén poging per box per keuze, zodat
 * een product dat niet in Kaching's lijst staat geen lus geeft; de bezoeker ziet dan
 * "kies een geur" en kiest zelf.
 */
const fillTried = new WeakMap();
let filling = false;

/** @param {Element} bar */
async function fillEmptyBoxes(bar) {
  if (filling || busy || !config.product) return;
  const tried = fillTried.get(bar) ?? new Set();
  fillTried.set(bar, tried);
  const empty = items(bar)
    .map((box, index) => ({ box, index }))
    .filter(({ box, index }) => index > 0 && !itemHandle(box) && !tried.has(index));
  if (!empty.length) return;

  filling = true;
  try {
    for (const { index } of empty) {
      tried.add(index);
      await setBox(bar, index, config.product, { silent: true });
    }
  } finally {
    filling = false;
  }
}

/** Sluiten zonder te kiezen: kies de geur die er stond, zodat de box niet leeg blijft. */
function restorePendingSwap() {
  if (!pendingSwap || !document.querySelector(MODAL)) return false;
  const { handle } = pendingSwap;
  pendingSwap = null;
  return pickInModal(handle);
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !pendingSwap) return;
    if (target.closest('.kaching-bundles__choose-product__product-button')) {
      pendingSwap = null;
      return;
    }
    const closing =
      target.closest('.kaching-bundles__choose-product__close') || target.classList.contains(MODAL.slice(1));
    if (closing && restorePendingSwap()) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape' && pendingSwap && restorePendingSwap()) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  true
);

/* ── Opbouw ──────────────────────────────────────────────────────────────────── */

/** @type {WeakMap<Element, string>} */
const lastSelected = new WeakMap();

/** @param {Element} root */
function trackSelection(root) {
  const bars = [...root.querySelectorAll('.kaching-bundles__bar')];
  const selected = bars.find((bar) => bar.classList.contains('kaching-bundles__bar--selected'));
  const id = selected?.getAttribute('data-deal-bar-id') || '';
  const previous = lastSelected.get(root);
  lastSelected.set(root, id);
  // De eerste keer is de voorselectie, geen keuze van de bezoeker.
  if (previous === undefined || previous === id || !selected) return;
  publish('pdp_pack_select', {
    tier: bars.indexOf(selected) + 1,
    label: selected.querySelector('.kaching-bundles__bar-title')?.textContent?.trim(),
  });
}

/**
 * "Kies je aantal: 3 Giftboxes" boven de kaarten. Zonder kaarten (geen deal, of een
 * wholesale-klant voor wie Kaching niet start) is er niets te kiezen en geen label.
 * @param {Element} root
 */
function renderLabel(root) {
  const bars = [...root.querySelectorAll('.kaching-bundles__bar')];
  const existing = root.previousElementSibling;
  let label = existing?.classList.contains('pack-label') ? existing : null;
  if (!bars.length || !config.quantity) {
    label?.remove();
    return;
  }
  if (!label) {
    label = document.createElement('p');
    label.className = 'pack-label';
    label.append(`${config.quantity}: `, document.createElement('strong'));
    root.before(label);
  }
  const selected = bars.find((bar) => bar.classList.contains('kaching-bundles__bar--selected'));
  const title = selected?.querySelector('.kaching-bundles__bar-title')?.textContent?.trim() || '';
  const strong = label.querySelector('strong');
  if (strong && strong.textContent !== title) strong.textContent = title;
}

/** @param {Element} root */
function enhance(root) {
  renderLabel(root);
  for (const bar of root.querySelectorAll('.kaching-bundles__bar')) {
    enhanceSavings(bar);
    enhanceExtras(bar);
    renderBoxes(bar);
    if (bar.classList.contains('kaching-bundles__bar--selected')) {
      fillEmptyBoxes(bar);
    } else {
      // Een nieuwe keuze van deze kaart mag opnieuw vullen.
      fillTried.delete(bar);
    }
  }
  trackSelection(root);
}

for (const root of document.querySelectorAll('kaching-bundle')) {
  // Vóór Kaching's eigen handlers: een tik op een keuze mag de kaart niet opnieuw
  // kiezen (de rijen staan in Kaching's <label>).
  root.addEventListener(
    'click',
    (event) => {
      const option = event.target instanceof Element ? event.target.closest('button.box-scents__option') : null;
      if (!option) return;
      event.preventDefault();
      event.stopPropagation();
      const bar = option.closest('.kaching-bundles__bar');
      if (bar) setBox(bar, Number(option.dataset.box), option.dataset.handle || '');
    },
    true
  );

  let queued = false;
  const run = () => {
    queued = false;
    enhance(root);
  };
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(run);
  }).observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
  run();
}
